import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

interface User {
  id: string;
  email: string;
  name: string | null;
  company: string | null;
  role: string;
}

interface Subscription {
  plan: string;
  status: string;
  workflowLimit: number | null;
  usageCount?: number;
  periodEnd?: string;
}

interface UsageInfo {
  remaining: number;
  limit: number | null;
  unlimited: boolean;
  canExecute: boolean;
  usageCount: number;
}

interface AuthContextType {
  user: User | null;
  subscription: Subscription | null;
  usageInfo: UsageInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (email: string, password: string, name: string, company?: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshUsage: () => Promise<void>;
  trackUsage: (workflowType: string, requestData?: Record<string, unknown>) => Promise<{ success: boolean; error?: string }>;
  canUseApp: () => boolean;
  getRemainingUsage: () => number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Session ID for anonymous users
function getSessionId(): string {
  let sessionId = localStorage.getItem('dz_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('dz_session_id', sessionId);
  }
  return sessionId;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usageInfo, setUsageInfo] = useState<UsageInfo | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // API helper with auth header
  const apiRequest = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-Session-Id': getSessionId(),
      ...(options.headers || {}),
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();
    
    if (!response.ok) {
      // Handle 401 specifically - clear auth state
      if (response.status === 401) {
        console.warn('Received 401 Unauthorized, clearing auth state');
        localStorage.removeItem('dz_token');
        setToken(null);
        setUser(null);
        setSubscription(null);
      }
      throw new Error(data.error || 'Request failed');
    }
    
    return data;
  }, [token]);

  // Load token and user on mount
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('dz_token');
      
      if (storedToken) {
        setToken(storedToken);
        try {
          const data = await apiRequest('/auth/me');
          setUser(data.user);
          setSubscription(data.subscription);
          setUsageInfo({
            remaining: data.usage.remaining,
            limit: data.usage.limit,
            unlimited: data.usage.unlimited,
            canExecute: data.usage.remaining !== 0 || data.usage.unlimited,
            usageCount: data.usage.limit ? data.usage.limit - data.usage.remaining : 0,
          });
        } catch (error) {
          // Token invalid or expired, but don't clear immediately
          console.warn('Auth token validation failed, will retry on next request:', error);
          // Don't clear token here - let it be cleared only on explicit 401 during actual requests
        }
      }
      
      // Check anonymous usage only if no token
      if (!storedToken) {
        await refreshUsage();
      }
      setIsLoading(false);
    };

    initAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save token when it changes
  useEffect(() => {
    if (token) {
      localStorage.setItem('dz_token', token);
    } else {
      localStorage.removeItem('dz_token');
    }
  }, [token]);

  const refreshUsage = async () => {
    try {
      const data = await apiRequest('/auth/usage');
      setUsageInfo({
        remaining: data.remaining,
        limit: data.limit,
        unlimited: data.unlimited,
        canExecute: data.canExecute,
        usageCount: data.usageCount,
      });
    } catch (error) {
      console.error('Failed to refresh usage:', error);
    }
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const data = await apiRequest('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      // Store token first
      localStorage.setItem('dz_token', data.token);
      setToken(data.token);
      setUser(data.user);
      setSubscription(data.subscription);
      
      // Use usage info directly from login response (if provided)
      if (data.usage) {
        setUsageInfo({
          remaining: data.usage.remaining,
          limit: data.usage.limit,
          unlimited: data.usage.unlimited,
          canExecute: data.usage.canExecute,
          usageCount: data.usage.usageCount || 0,
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Login failed' };
    }
  };

  const register = async (
    email: string, 
    password: string, 
    name: string, 
    company?: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const data = await apiRequest('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, name, company }),
      });

      // Store token first
      localStorage.setItem('dz_token', data.token);
      setToken(data.token);
      setUser(data.user);
      setSubscription(data.subscription);
      
      // Use usage info directly from response (if provided)
      if (data.usage) {
        setUsageInfo({
          remaining: data.usage.remaining,
          limit: data.usage.limit,
          unlimited: data.usage.unlimited,
          canExecute: data.usage.canExecute,
          usageCount: data.usage.usageCount || 0,
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Register error:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Registration failed' };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await apiRequest('/auth/logout', { method: 'POST' });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setToken(null);
      setUser(null);
      setSubscription(null);
      localStorage.removeItem('dz_token');
      await refreshUsage();
    }
  };

  const trackUsage = async (
    workflowType: string,
    requestData?: Record<string, unknown>
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const data = await apiRequest('/auth/usage/track', {
        method: 'POST',
        body: JSON.stringify({ workflowType, requestData }),
      });

      // Update local usage info with response
      setUsageInfo({
        remaining: data.remaining,
        limit: data.limit,
        unlimited: data.unlimited,
        canExecute: data.canExecute,
        usageCount: data.usageCount,
      });

      return { success: true };
    } catch (error) {
      console.error('Track usage error:', error);
      // Check if it's a limit reached error
      if (error instanceof Error && error.message.includes('limit')) {
        await refreshUsage();
        return { success: false, error: 'Usage limit reached' };
      }
      return { success: false, error: error instanceof Error ? error.message : 'Failed to track usage' };
    }
  };

  const canUseApp = (): boolean => {
    return usageInfo?.canExecute ?? false;
  };

  const getRemainingUsage = (): number => {
    if (usageInfo?.unlimited) return -1;
    return usageInfo?.remaining ?? 0;
  };

  const value: AuthContextType = {
    user,
    subscription,
    usageInfo,
    isAuthenticated: !!user,
    isLoading,
    token,
    login,
    register,
    logout,
    refreshUsage,
    trackUsage,
    canUseApp,
    getRemainingUsage,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
